<template lang="pug">
    div(class="_crsl", :style="{ width, height }")
        div(class="_crsh",
            @wheel.prevent="wheel")
            div(ref="outer",
                class="_crso",
                :class="{ _cios: $device.ios, _csnap: !$device.hasMouse }",
                :style="hidebar",
                @scroll="scroll")
                div(ref="inner",
                    class="_crsi",
                    @mousedown="mousedown",
                    @touchstart="touchstart")
                    slot
        ol(class="_crst", :class="{ _hide: child.length < 2 }", role="tablist")
            li(v-for="(c,i) in child",
                role="tab",
                :aria-selected="active == i",
                @click="goto(i)")
</template>

<script>
    import $device from '../../prototype/device';
    import $util from '../../prototype/util';
    import resize from '../private/resize';
    import { scrollProps } from '../private/util';

    const haveSnapScroll =
        'scrollSnapType' in document.documentElement.style ||
        'webkitScrollSnapType' in document.documentElement.style ||
        'msScrollSnapType' in document.documentElement.style;

    const wheelThreshold = 1;
    const wheelThrottle = 150;


    /**
     * This is a user interface element that allows different content blocks to share the same
     * screen space in a mutually exclusive manner. Unlike `ib-tabs`, no heading is provided;
     * panning is done exclusively by dragging the content area. This is implemented using scroll
     * snap on devices that support it.
     * @see Each seat is represented as an instance of [ib-col](#ibcol).
     */
    export default {
        name: 'ib-carousel',
        mixins: [resize],

        props: {
            /**
             * The index of the seat to display. If not specified, the first seat will be shown.
             */
            value: Number,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String
        },

        data() {
            return {
                child: [],
                active: -1,
                grab: false,
                fm: false,
                sl: 0,
                cx: 0,
                ts: 0,
                tw: 0,
                left: 0,
                observer: null,
                rseW: true,
                rseH: true
            };
        },

        computed: {
            sp() {
                const props = scrollProps();
                const rtl = this.$rtl;
                return {
                    base: rtl ? props.rtlBase : 0,
                    step: rtl ? props.rtlStep : 1,
                    barHeight: props.barHeight
                };
            },

            hidebar() {
                const sx = this.sp.barHeight;
                return {
                    marginBottom: -(sx || 16) + 'px',
                    paddingBottom: sx ? 0 : '16px'
                };
            }
        },

        watch: {
            value: 'goto',
            dir: 'resize'
        },

        mounted() {
            this._mutate();
            if (window.MutationObserver) {
                this.observer = new MutationObserver(this._mutate);
                this.observer.observe(this.$refs.inner, { childList: true });
            }
            this.$nextTick(() => {
                this.goto(this.value, 'auto');
                this.scroll();
            });
        },

        beforeDestroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        },

        methods: {
            /**
             * Navigate to the seat before the active one, if any.
             */
            prev() {
                if (this.active > 0) {
                    this.goto(this.active - 1);
                }
            },

            /**
             * Navigate to the seat after the active one, if any.
             */
            next() {
                if (this.active < this.child.length - 1) {
                    this.goto(this.active + 1);
                }
            },

            /**
             * Navigate to a seat by index.
             */
            goto(index, behavior = 'smooth') {
                const outer = this.$refs.outer;
                if (outer) {
                    this.end();
                    index = Math.round(Math.max(0, Math.min(this.child.length - 1, index))) | 0;
                    const left = this.sp.base * outer.scrollWidth + (this.sp.step * Math.round(index) - this.sp.base) * outer.clientWidth;
                    if (left != outer.scrollLeft) {
                        // this.active will be updated by scroll event
                        this.left = left;
                        outer.scrollTo({ left, behavior });
                        return true;
                    } else {
                        this.scroll();
                    }
                }
            },

            /**
             * Capture or release mouse.
             * @private
             */
            capture(fn, event) {
                let outer = this.$refs.outer;
                if (outer[fn]) {
                    outer[fn]();
                } else {
                    document.documentElement[event]('mouseout', this.mouseout);
                    outer = document.body;
                }
                outer[event]('mousemove', this.mousemove);
                outer[event]('mouseup', this.mouseup);
            },

            /**
             * Mouse down event.
             * @private
             */
            mousedown(e) {
                const outer = this.$refs.outer;
                if (!e.button) {
                    this.capture('setCapture', 'addEventListener');
                    this.start();
                    this.sl = outer.scrollLeft;
                    this.cx = e.clientX;
                }
            },

            mouseup(e) {
                this.snap(this.cx - e.clientX);
                e.preventDefault();
            },

            mousemove(e) {
                this.move(e.clientX);
                e.preventDefault();
            },

            mouseout(e) {
                if (e.target === document.documentElement) {
                    this.mouseup(e);
                }
            },

            /**
             * Touch start event.
             * @private
             */
            touchstart(e) {
                const outer = this.$refs.outer;
                if (e.touches.length == 1) {
                    if ($device.hasMouse) {
                        outer.addEventListener('touchmove', this.touchmove);
                    }
                    document.addEventListener('touchend', this.touchend, false);
                    this.start();
                    this.sl = outer.scrollLeft;
                    this.cx = e.touches[0].clientX;
                }
            },

            touchmove(e) {
                this.move(e.touches[0].clientX);
                e.preventDefault();
            },

            touchend(e) {
                const outer = this.$refs.outer;
                outer.removeEventListener('touchmove', this.touchmove);
                document.removeEventListener('touchend', this.touchend, false);
                if (!$device.hasMouse && haveSnapScroll) {
                    this.end();
                } else {
                    this.snap(outer.scrollLeft - this.sl);
                }
            },

            /**
             * Mouse wheel event.
             * @private
             */
            wheel(e) {
                const outer = this.$refs.outer;
                const now = Date.now();
                if (this.tw < now - wheelThrottle) {
                    const x = e.deltaX;
                    const y = e.deltaY;
                    const go =
                        x < -wheelThreshold || x > wheelThreshold ? x :
                        y < -wheelThreshold || y > wheelThreshold ? y : 0;
                    if (go) {
                        this.tw = now;
                        if (this.goto(this.active + (go > 0 ? 1 : -1))) {
                            outer.classList.add('_crsw');
                        }
                    }
                }
            },

            /**
             * Set scroll position during drag.
             * @private
             */
            move(x) {
                const outer = this.$refs.outer;
                // Prevent click event on children after first move
                if (this.fm) {
                    this.fm = false;
                    outer.classList.add('_crsp');
                }
                outer.scrollTo({
                    left: this.sl + this.cx - x
                });
            },

            /**
             * Begin grabbing after mouse or touch start.
             * @private
             */
            start() {
                this.grab = true;
                this.fm = true;
                this.ts = Date.now();
                if ($device.hasMouse) {
                    const outer = this.$refs.outer;
                    outer.classList.add('_crsg');
                }
            },

            /**
             * End grabbing after mouse or touch end.
             * @private
             */
            end() {
                if (this.grab) {
                    this.grab = false;
                    this.capture('releaseCapture', 'removeEventListener');
                    const outer = this.$refs.outer;
                    outer.classList.remove('_crsp');
                    outer.classList.remove('_crsg');
                }
            },

            /**
             * Scroll into view with momentum after mouse or touch end.
             * @private
             */
            snap(delta) {
                const outer = this.$refs.outer;
                const duration = Date.now() - this.ts;
                const cw = outer.clientWidth;
                if (duration > 20) {
                    delta += delta * Math.abs(delta / duration);
                    delta = Math.min(cw, Math.max(-cw, delta));
                }
                this.goto((this.sp.base * outer.scrollWidth + this.sp.step * (this.sl + delta)) / cw - this.sp.base);
            },

            /**
             * Scroll event.
             * @private
             */
            scroll() {
                const outer = this.$refs.outer;
                if (outer) {
                    const width = outer.clientWidth;
                    if (width) {
                        const index = Math.round((this.sp.base * outer.scrollWidth + this.sp.step * outer.scrollLeft) / outer.clientWidth - this.sp.base);
                        if (this.active !== index) {
                            this.active = index;
                            this.$emit('input', index);
                        }
                        if (outer.scrollLeft === this.left) {
                            outer.classList.remove('_crsw');
                        }
                    }
                }
            },

            /**
             * Resize event.
             * @private
             */
            resize() {
                this.goto(this.active, 'auto');
            },

            /**
             * Recompute the child list.
             * @private
             */
            _mutate() {
                const inner = this.$refs.inner;
                const child = [].slice.call(inner.children);
                this.child = child;
                if (this.active >= 0) {
                    this.goto(this.active, 'auto');
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._crsl {
        display: flex;
        flex-direction: column;
        position: relative;
        width: 100%;
        max-width: 100%;
        max-height: 100%;
    }
    ._crsh {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
    }
    ._crsh {
        overflow: hidden;
    }
    ._crso {
        overflow-x: scroll;
        overflow-y: auto;
        cursor: -webkit-grab;
        cursor: grab;
    }
    ._crsi {
        display: flex;
        flex-direction: row;
        flex: 1 0 auto;
        > * {
            scroll-snap-align: none start;
        }
        img {
            -webkit-user-drag: none;
            user-drag: none;
        }
    }
    ._cios {
        > * {
            /** iOS cannot set snap align on axes independently */
            scroll-snap-align: start;
        }
    }
    ._crst {
        display: flex;
        flex-direction: row;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 8px 0;
        list-style: none;
        line-height: 0;
        > li {
            margin: 0;
            padding: 4px;
            opacity: .2;
            cursor: pointer;
            border-radius: 50%;
            transition: opacity .2s linear;
            &[aria-selected=true] {
                opacity: .5;
            }
            &:hover {
                opacity: .4;
            }
            &::before {
                content: '';
                display: inline-block;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: @fg;
            }
        }
        &._hide {
            visibility: hidden;
        }
    }
    ._crsp,
    ._crsw {
        pointer-events: none;
    }
    ._csnap {
        -webkit-scroll-snap-type: mandatory;
        -webkit-scroll-snap-destination: 0% 0%;
        -webkit-scroll-snap-points-x: repeat(100%);
        -webkit-overflow-scrolling: touch;
        -ms-scroll-snap-type: mandatory;
        scroll-snap-type: mandatory;
        scroll-snap-type: x mandatory;
        scroll-snap-points-x: repeat(100%);
    }
    ._crsg {
        cursor: -webkit-grabbing;
        cursor: grabbing;
        -webkit-scroll-snap-type: none;
        -ms-scroll-snap-type: none;
        scroll-snap-type: none;
    }
</style>

<docs>
    Basic Example:
    ```jsx
    <ib-carousel>
        <ib-col><img src="https://www.interactivebrokers.com/images/2015/products_overview_futures_wheat.jpg" /></ib-col>
        <ib-col><img src="https://www.interactivebrokers.com/images/2015/products_overview_hedge_fund.jpg" /></ib-col>
        <ib-col><img src="https://www.interactivebrokers.com/images/2015/products_overview_metal_bars.jpg" /></ib-col>
        <ib-col><img src="https://www.interactivebrokers.com/images/2015/products_overview_stocks.jpg" /></ib-col>
    </ib-carousel>
    ```
</docs>
